import * as React from "react";
import { useKernelClient } from "./useKernelClient";
import { useUserOperationRunner, ContractCall } from "./useUserOperationRunner";
import { encodeFunctionData, Hex, decodeErrorResult } from "viem";
import { ERROR_MAPPING } from "../constants/contractAbis";

interface ApprovalTransferData {
    batchData: { abi: any[]; functionName: string; args: any[]; };
}

export interface UseApprovalExecutionProps {
    dotNetRef: any;
    approvalTransfers: ApprovalTransferData[];
    contractAddress: Hex;
    contractAbi?: any[]; // Full contract ABI for error decoding
    bundlerRpc: string;
    paymasterRpc: string;
    isDevelopment?: boolean;
    selectedPrivyWalletAddress?: string;
    network?: string;
    approvalBatchSize?: number;
}

interface ApprovalEvent {
    type: "status" | "progress" | "error" | "result";
    category?: "approval" | "wallet" | "transaction";
    status?: "initializing" | "preparing" | "signing" | "executing" | "confirming" | "idle" | "success";
    message?: string;
    current?: number;
    total?: number;
    transactionHash?: string;
    success?: boolean;
    transactionHashes?: string[];
    approvedTokens?: number;
    error?: string;
}

/**
 * Manages the execution of approval transactions in a blockchain context.
 *
 * This function initializes the approval process, handles transaction execution, monitors progress, and emits events to a .NET backend. It decodes revert errors using the contract ABI and manages the state of the execution, including error handling and completion notifications. The function utilizes hooks to manage side effects and state updates throughout the approval lifecycle.
 *
 * @param props - The properties required for approval execution, including dotNetRef, approvalTransfers, contractAddress, contractAbi, bundlerRpc, paymasterRpc, isDevelopment, and selectedPrivyWalletAddress.
 * @returns An object containing the initialization status, execution status, completion status, any errors, completed transaction hashes, current batch, and total batches.
 */
export const useApprovalExecution = (props: UseApprovalExecutionProps) => {
    const { dotNetRef, approvalTransfers, contractAddress, contractAbi, bundlerRpc, paymasterRpc, isDevelopment, selectedPrivyWalletAddress, network, approvalBatchSize } = props;
    
    // Helper function to decode revert errors
    const decodeRevertError = React.useCallback((errorMessage: string): string => {
        if (!contractAbi) return errorMessage;
        
        // Extract hex error code from the error message
        const hexMatch = errorMessage.match(/0x[a-fA-F0-9]+/);
        if (!hexMatch) return errorMessage;
        
        const errorHex = hexMatch[0] as Hex;
        
        // Try to decode the error using the contract ABI
        try {
            // Filter for error definitions in the ABI
            const errorAbi = contractAbi.filter(item => item.type === 'error');
            if (errorAbi.length === 0) return errorMessage;
            
            // Try to decode with viem's decodeErrorResult
            const decodedError = decodeErrorResult({
                abi: contractAbi,
                data: errorHex
            });
            
            // Format the decoded error
            if (decodedError.errorName) {
                let decodedMessage = `Contract Error: ${decodedError.errorName}`;
                if (decodedError.args && Object.keys(decodedError.args).length > 0) {
                    const args = Object.entries(decodedError.args)
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(', ');
                    decodedMessage += ` (${args})`;
                }
                return decodedMessage;
            }
        } catch (decodeError) {
            console.log('Could not decode error with ABI:', decodeError);
            
            // Use the imported error mapping
            const errorCode = errorHex.toLowerCase();
            const knownError = ERROR_MAPPING[errorCode];
            if (knownError) {
                return `${knownError.message}\n\n(Error: ${knownError.name})`;
            }
        }
        
        return errorMessage;
    }, [contractAbi]);

    const { client, isInitializing: isClientInitializing, error: clientError } = useKernelClient({ 
        bundlerRpc, 
        paymasterRpc, 
        isDevelopment,
        selectedWalletAddress: selectedPrivyWalletAddress,
        network
    });
    
    const [hasStarted, setHasStarted] = React.useState(false);
    const [isComplete, setIsComplete] = React.useState(false);
    const eventQueueRef = React.useRef<Promise<void>>(Promise.resolve());

    const stableApprovalTransfers = React.useMemo(() => approvalTransfers, [JSON.stringify(approvalTransfers)]);

    // Keep UI updates in the exact order they are emitted. The approval flow can
    // produce several status changes in one render, and concurrent interop calls
    // allow an older message to overwrite a newer one in Blazor.
    const emitEvent = React.useCallback((event: ApprovalEvent) => {
        const nextEvent = eventQueueRef.current.then(async () => {
            try {
                await dotNetRef.invokeMethodAsync("OnEventCallback", JSON.stringify(event));
            } catch (error) {
                console.error("[ApprovalExecution] Failed to deliver progress event:", error);
            }
        });

        eventQueueRef.current = nextEvent;
        return nextEvent;
    }, [dotNetRef]);

    const { executeBundled, executeBatchedApprovals, isExecuting, error: runnerError, completedTxs, currentBatch, totalBatches } = useUserOperationRunner(client, dotNetRef, emitEvent);

    // Execute approval transfers
    const executeApproval = React.useCallback(async () => {
        await emitEvent({
            type: "status",
            category: "approval",
            status: "preparing",
            message: "Preparing approval transactions..."
        });

        const calls: ContractCall[] = stableApprovalTransfers.map(transfer => ({
            to: contractAddress,
            value: BigInt(0),
            data: encodeFunctionData({ ...transfer.batchData }),
        }));

        if (approvalBatchSize !== undefined) {
            await executeBatchedApprovals(calls, approvalBatchSize);
            return;
        }

        await emitEvent({
            type: "status",
            category: "approval",
            status: "executing",
            message: `Bundling ${calls.length} approval${calls.length > 1 ? 's' : ''} into one transaction...`
        });

        await executeBundled(calls);
    }, [stableApprovalTransfers, contractAddress, executeBundled, executeBatchedApprovals, approvalBatchSize, emitEvent]);

    // Monitor initialization
    React.useEffect(() => {
        if (isClientInitializing) {
            void emitEvent({
                type: "status",
                category: "approval",
                status: "initializing",
                message: "Connecting to wallet..."
            });
        }
    }, [isClientInitializing, emitEvent]);

    // Monitor transaction completions (for logging only, actual hashes sent in result event)
    React.useEffect(() => {
        if (completedTxs.length > 0) {
            console.log('[ApprovalExecution] Completed transactions:', completedTxs);
        }
    }, [completedTxs]);

    // Handle errors
    React.useEffect(() => {
        const error = clientError || runnerError;
        if (error && !isComplete) {
            // Decode the error message if it contains a revert reason
            const decodedError = decodeRevertError(error);
            
            void emitEvent({
                type: "error",
                category: error === clientError ? "wallet" : "transaction",
                message: decodedError,
                error: decodedError
            });
            setIsComplete(true);
        }
    }, [clientError, runnerError, isComplete, emitEvent, decodeRevertError]);

    // Handle completion
    React.useEffect(() => {
        if (!isExecuting && !runnerError && !clientError && currentBatch === totalBatches && completedTxs.length > 0 && !isComplete && hasStarted) {
            console.log('[ApprovalExecution] Approval complete! Transaction hashes:', completedTxs);
            console.log('[ApprovalExecution] Sending result event with', completedTxs.length, 'transaction hashes');
            
            // Send result event with transaction hashes
            void emitEvent({
                type: "result",
                success: true,
                transactionHashes: completedTxs,
                approvedTokens: stableApprovalTransfers.length,
                message: `Successfully approved ${stableApprovalTransfers.length} tokens`
            });
            
            // Don't send a separate success status - the result event handles completion
            
            setIsComplete(true);
        }
    }, [isExecuting, runnerError, clientError, currentBatch, totalBatches, completedTxs, isComplete, hasStarted, stableApprovalTransfers.length, emitEvent]);

    // Auto-execute when client is ready
    React.useEffect(() => {
        if (!isClientInitializing && client && !hasStarted) {
            setHasStarted(true);
            void (async () => {
                await emitEvent({
                    type: "status",
                    category: "approval",
                    status: "signing",
                    message: "Setting up wallet for EIP-7702..."
                });
                await executeApproval();
            })();
        }
    }, [isClientInitializing, client, hasStarted, executeApproval, emitEvent]);

    return {
        isInitializing: isClientInitializing,
        isExecuting,
        isComplete,
        error: clientError || runnerError,
        completedTxs,
        currentBatch,
        totalBatches
    };
};
